import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Container,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  Stack,
  Text,
  useColorModeValue,
  Alert,
  AlertIcon,
  FormErrorMessage,
  Link as ChakraLink,
  InputGroup,
  InputRightElement,
  IconButton,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [generalError, setGeneralError] = useState('');
  
  const { login, error } = useAuth();
  const navigate = useNavigate();
  
  const validateForm = () => {
    let isValid = true;
    
    // メールバリデーション
    if (!email) {
      setEmailError('メールアドレスは必須です');
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('有効なメールアドレスを入力してください');
      isValid = false;
    } else {
      setEmailError('');
    }
    
    // パスワードバリデーション
    if (!password) {
      setPasswordError('パスワードは必須です');
      isValid = false;
    } else {
      setPasswordError('');
    }
    
    return isValid;
  };
  
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setGeneralError('');
    
    try {
      const success = await login(email, password);
      
      if (success) {
        navigate('/dashboard');
      } else {
        setGeneralError('ログインに失敗しました。メールアドレスとパスワードを確認してください。');
      }
    } catch (error) {
      console.error('Login error:', error);
      setGeneralError('ログイン処理中にエラーが発生しました。');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Container maxW="lg" py={{ base: '12', md: '24' }} px={{ base: '0', md: '8' }}>
      <Stack spacing="8">
        <Stack spacing="6" align="center">
          <Logo width="120px" />
          <Stack spacing={{ base: '2', md: '3' }} textAlign="center">
            <Heading size={{ base: 'md', md: 'lg' }}>GenX AI-OCR</Heading>
            <Text color="fg.muted">アカウントにログインしてください</Text>
          </Stack>
        </Stack>
        
        <Box
          py={{ base: '8', md: '8' }}
          px={{ base: '4', md: '10' }}
          bg={useColorModeValue('white', 'gray.800')}
          boxShadow={{ base: 'none', md: 'xl' }}
          borderRadius={{ base: 'none', md: 'xl' }}
        >
          <form onSubmit={handleLogin}>
            <Stack spacing="6">
              {/* エラーメッセージ */}
              {(generalError || error) && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  {generalError || error}
                </Alert>
              )}
              
              <Stack spacing="5">
                {/* メールアドレス入力 */}
                <FormControl isInvalid={!!emailError}>
                  <FormLabel htmlFor="email">メールアドレス</FormLabel>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    autoComplete="email"
                  />
                  {emailError && <FormErrorMessage>{emailError}</FormErrorMessage>}
                </FormControl>
                
                {/* パスワード入力 */}
                <FormControl isInvalid={!!passwordError}>
                  <FormLabel htmlFor="password">パスワード</FormLabel>
                  <InputGroup>
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                    />
                    <InputRightElement>
                      <IconButton
                        aria-label={showPassword ? 'パスワードを隠す' : 'パスワードを表示'}
                        icon={showPassword ? <FiEyeOff /> : <FiEye />}
                        variant="ghost"
                        size="sm"
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    </InputRightElement>
                  </InputGroup>
                  {passwordError && <FormErrorMessage>{passwordError}</FormErrorMessage>}
                </FormControl>
              </Stack>
              
              <HStack justify="space-between">
                {/* ログイン情報を記憶 */}
                <Checkbox
                  isChecked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                >
                  ログイン情報を記憶
                </Checkbox>
                
                {/* パスワードを忘れた場合 */}
                <ChakraLink as={RouterLink} to="/forgot-password" color="brand.500" fontSize="sm">
                  パスワードを忘れた場合
                </ChakraLink>
              </HStack>
              
              {/* ログインボタン */}
              <Button
                type="submit"
                colorScheme="brand"
                size="lg"
                fontSize="md"
                isLoading={isSubmitting}
                loadingText="ログイン中..."
              >
                ログイン
              </Button>
              
              {/* 新規登録リンク */}
              <HStack spacing="1" justify="center">
                <Text>アカウントをお持ちでない場合は</Text>
                <ChakraLink as={RouterLink} to="/register" color="brand.500">
                  新規登録
                </ChakraLink>
              </HStack>
            </Stack>
          </form>
        </Box>
      </Stack>
    </Container>
  );
};

export default Login;